import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

function Interval({ id, seconds, onChange, disabled }) {
  const [unit, setUnit] = useState(seconds % 60 === 0 ? "min" : "s");
  const shown = unit === "min" ? seconds / 60 : seconds;
  return (
    <div className="flex gap-1">
      <Input data-testid={`${id}-value`} type="number" min="1" step="any" disabled={disabled} value={shown}
        onChange={(e) => onChange(Math.round(Number(e.target.value || 0) * (unit === "min" ? 60 : 1)))} className="h-11 sm:h-9 w-24" />
      <select data-testid={`${id}-unit`} disabled={disabled} value={unit} onChange={(e) => setUnit(e.target.value)} className="h-11 sm:h-9 rounded-md border px-2 bg-white text-sm">
        <option value="s">seconds</option><option value="min">minutes</option>
      </select>
    </div>
  );
}

const Row = ({ title, hint, children }) => (
  <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-center py-2 border-b last:border-0">
    <div><p className="font-medium text-sm">{title}</p>{hint && <p className="text-xs text-stone-500">{hint}</p>}</div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export function SyncScheduleForm({ value, limits, onSave, saving, testid = "sync-form" }) {
  const [f, setF] = useState(value);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const hours = (k) => (
    <Input data-testid={`${testid}-${k}`} type="number" min="0.1" step="0.25" value={f[k]} onChange={(e) => set(k)(Number(e.target.value))} className="h-11 sm:h-9 w-24" />
  );
  const tooFast = ["stage1_seconds", "stage2_seconds", "stage3_seconds"].some((k) => f[k] < limits.min_seconds || f[k] > limits.max_seconds);
  const badOrder = !(f.stage3_hours > 0 && f.stage2_hours > f.stage3_hours);
  return (
    <div data-testid={testid} className="space-y-1">
      <Row title="Stage 1 — Early bidding" hint={`From bidding opening until Stage 2 starts`}>
        <span className="text-xs">every</span><Interval id={`${testid}-s1`} seconds={f.stage1_seconds} onChange={set("stage1_seconds")} />
      </Row>
      <Row title="Stage 2 — Approaching close" hint="Starts this many hours before close">
        {hours("stage2_hours")}<span className="text-xs">h before close, every</span>
        <Interval id={`${testid}-s2`} seconds={f.stage2_seconds} onChange={set("stage2_seconds")} />
      </Row>
      <Row title="Stage 3 — Final bidding" hint="Starts this many hours before close, until bidding is complete">
        {hours("stage3_hours")}<span className="text-xs">h before close</span>
        <select data-testid={`${testid}-s3-mode`} value={f.stage3_mode} onChange={(e) => set("stage3_mode")(e.target.value)} className="h-11 sm:h-9 rounded-md border px-2 bg-white text-sm">
          <option value="realtime">Real Time (fastest supported)</option><option value="interval">Custom interval</option>
        </select>
        {f.stage3_mode === "interval" && <Interval id={`${testid}-s3`} seconds={f.stage3_seconds} onChange={set("stage3_seconds")} />}
      </Row>
      <Row title="Final reconciliation after close" hint="One complete read shortly after close (retried up to 3 times if incomplete)">
        <Switch data-testid={`${testid}-final`} checked={f.final_reconcile} onCheckedChange={set("final_reconcile")} />
      </Row>
      {f.stage3_mode === "realtime" && <p data-testid={`${testid}-realtime-note`} className="text-xs rounded-md bg-[#FEF3C7] text-[#92400E] p-2">{limits.realtime_note}</p>}
      {tooFast && <p data-testid={`${testid}-limit-error`} className="text-xs text-red-700">Intervals must be between {limits.min_seconds} seconds and {limits.max_seconds / 3600} hours.</p>}
      {badOrder && <p data-testid={`${testid}-order-error`} className="text-xs text-red-700">Stage 3 must start closer to the close than Stage 2.</p>}
      <Button data-testid={`${testid}-save`} disabled={saving || tooFast || badOrder} onClick={() => onSave(f)} className="h-11 sm:h-9 bg-amber-800 hover:bg-amber-900 text-white">{saving ? "Saving…" : "Save schedule"}</Button>
    </div>
  );
}
